import { PurchaserFilesApiData } from 'apiProviders'

/** Brand-aligned colours for file-extension pills (mirrors the design system). */
const EXT_COLOR: Record<string, string> = {
    PDF: '#E85959',
    DOC: '#4F9BF0',
    DOCX: '#4F9BF0',
    XLS: '#2DBA78',
    XLSX: '#2DBA78',
    CSV: '#2DBA78',
    JPG: '#1FB2A6',
    JPEG: '#1FB2A6',
    PNG: '#1FB2A6',
    GIF: '#1FB2A6',
    WEBP: '#1FB2A6',
    HEIC: '#1FB2A6',
    ZIP: '#DD9B34',
    RAR: '#DD9B34',
    EML: '#A47EF0',
    MSG: '#A47EF0',
}

const DEFAULT_EXT_COLOR = '#7C8290'

/** Upper-cased file extension, e.g. "report.pdf" → "PDF". Empty string when none. */
export const getExt = (name: string) => {
    const base = name.replace(/\/$/, '')
    const dot = base.lastIndexOf('.')
    return dot < 0 ? '' : base.slice(dot + 1).toUpperCase()
}

export const extColor = (name: string) => EXT_COLOR[getExt(name)] ?? DEFAULT_EXT_COLOR

/** Extensions we can render in the in-app preview modal (PDF / image / email). */
const PREVIEWABLE = new Set([
    'PDF',
    'PNG',
    'JPG',
    'JPEG',
    'GIF',
    'WEBP',
    'SVG',
    'BMP',
    'EML',
    'MSG',
])

export const isPreviewable = (name: string) => PREVIEWABLE.has(getExt(name))

/** Human-readable size: "412 KB", "4.2 MB", "1.3 GB". */
export const formatBytes = (bytes?: number) => {
    if (!bytes) return '—'
    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${Math.round(kb)} KB`
    const mb = kb / 1024
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    return `${(mb / 1024).toFixed(1)} GB`
}

/** Parent folder path of an item's `name` ("a/b/c" → "a/b", "a" → ""). */
export const parentOf = (name: string) => {
    const i = name.lastIndexOf('/')
    return i < 0 ? '' : name.slice(0, i)
}

/** Final path segment ("a/b/c" → "c"). */
export const lastSegment = (name: string) => {
    const base = name.replace(/\/$/, '')
    const i = base.lastIndexOf('/')
    return i < 0 ? base : base.slice(i + 1)
}

/** Direct child folders of the folder at `folderName` ("" = root). */
export const childFolders = (files: PurchaserFilesApiData[], folderName: string) =>
    files.filter(f => f.isFolder && parentOf(f.name) === folderName)

/** Direct child files of the folder at `folderName` ("" = root). */
export const childFiles = (files: PurchaserFilesApiData[], folderName: string) =>
    files.filter(f => !f.isFolder && parentOf(f.name) === folderName)

/** Total files (recursive) contained under `folderName` ("" = root → every file). */
export const countFilesUnder = (files: PurchaserFilesApiData[], folderName: string) =>
    files.filter(
        f => !f.isFolder && (folderName === '' || f.name.startsWith(folderName + '/')),
    ).length
